import { AppIcon, InfoModal } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Typography, SxProps, Theme } from '@mui/material';

const STRINGS = appStrings.modules.majorsGuide.careerRelevance.whyWeBuiltThisToolModal;

export interface WhyWeBuiltThisToolModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * WhyWeBuiltThisToolModal - Informational modal explaining the purpose of the
 * Career Relevance tool. Uses card-based layout with featured icons matching
 * the Figma design.
 */
export function WhyWeBuiltThisToolModal({ open, onClose }: WhyWeBuiltThisToolModalProps) {
  const headerContent = (
    <Box sx={headerTextContainerSx}>
      <Typography sx={headerTitleSx}>
        {appStrings.modules.majorsGuide.careerRelevance.whyWeBuiltThisTool}
      </Typography>
      <Typography sx={headerSubtitleSx}>{STRINGS.subtitle}</Typography>
    </Box>
  );

  return (
    <InfoModal
      open={open}
      onClose={onClose}
      width="sm"
      headerContent={headerContent}
      featuredIcon={{
        iconName: 'InfoCircle',
        size: 'lg',
        color: 'Gray/700',
        border: true,
      }}
      bodyContent={
        <Box sx={cardsContainerSx}>
          {/* Data-Driven Insights Card */}
          <Box sx={cardSx}>
            <Box sx={cardIconCircleSx}>
              <AppIcon iconName="Dataflow03" diameter="20px" color="Colors/Base/White" />
            </Box>
            <Box sx={cardTextContainerSx}>
              <Typography sx={cardTitleSx}>{STRINGS.dataDrivenInsightsTitle}</Typography>
              <Typography sx={cardDescriptionSx}>
                {STRINGS.dataDrivenInsightsDescription}
              </Typography>
            </Box>
          </Box>

          {/* Make Informed Decisions Card */}
          <Box sx={cardSx}>
            <Box sx={cardIconCircleSx}>
              <AppIcon iconName="CursorClick01" diameter="20px" color="Colors/Base/White" />
            </Box>
            <Box sx={cardTextContainerSx}>
              <Typography sx={cardTitleSx}>{STRINGS.makeInformedDecisionsTitle}</Typography>
              <Typography sx={cardDescriptionSx}>
                {STRINGS.makeInformedDecisionsDescription}
              </Typography>
            </Box>
          </Box>
        </Box>
      }
    />
  );
}

// Header styles
const headerTextContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const headerTitleSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-lg-semibold'],
  color: backrs.colors['Colors/Text/text-primary'],
});

const headerSubtitleSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-regular'],
  color: backrs.colors['Colors/Text/text-primary'],
});

// Cards container
const cardsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

// Individual card styles
const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
  p: backrs.spacing['spacing-xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const cardIconCircleSx: SxProps<Theme> = ({ backrs }) => ({
  width: '40px',
  height: '40px',
  minWidth: '40px',
  borderRadius: '50%',
  backgroundColor: backrs.colors['Brand/500'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const cardTextContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  flex: 1,
});

const cardTitleSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-md-bold'],
  color: backrs.colors['Colors/Text/text-secondary'],
});

const cardDescriptionSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-regular'],
  color: backrs.colors['Colors/Text/text-tertiary'],
});
